import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiAlertTriangle,
  FiX,
  FiArrowLeft,
  FiSave,
  FiClock
} from "react-icons/fi";
import Swal from "sweetalert2";

const ReceivePO = () => {
  const { poNumber } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState([]);
  const [outlet, setOutlet] = useState({ name: "" });
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isFullyReceived, setIsFullyReceived] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!poNumber) return;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Akses Ditolak",
          text: "Token tidak ditemukan, silakan login kembali.",
          customClass: { popup: "rounded-3xl" },
        });
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const result = await res.json();
        if (result.data) {
          const poItems = (result.data.items || []).map((item) => {
            const totalReceived = (item.logs || []).reduce(
              (acc, log) => acc + Number(log.qty || 0),
              0
            );
            const remaining = Math.max((item.qty || 0) - totalReceived, 0);
            return {
              id: item.obat?.id,
              name: item.obat?.name || "Obat",
              dosis: item.obat?.dosis || "",
              qtyOrder: item.qty || 0,
              logs: item.logs || [],
              totalReceived,
              remainingQty: remaining,
              qtyReceive: remaining,
              expiredAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            };
          });

          setFormData(poItems);
          setOutlet(result.data.outlet || { name: "Outlet" });
        }
      } catch (err) {
        console.error("Gagal mengambil data PO:", err);
        Swal.fire({
          icon: "error",
          title: "Gagal Memuat PO",
          text: "Terjadi kesalahan saat mengambil detail pesanan.",
          customClass: { popup: "rounded-3xl" },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [poNumber]);

  useEffect(() => {
    const isAllReceived = formData.every(
      (item) => Number(item.qtyReceive) >= item.remainingQty
    );
    setIsFullyReceived(isAllReceived);
  }, [formData]);

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    if (field === "qtyReceive") {
      const max = updated[index].remainingQty;
      const safeVal = Math.min(Math.max(Number(value) || 0, 0), max);
      updated[index][field] = safeVal;
    } else {
      updated[index][field] = value;
    }
    setFormData(updated);
  };

  const confirmSubmit = async () => {
    const result = await Swal.fire({
      title: `Konfirmasi Penerimaan PO ${poNumber}?`,
      text: "Stok obat akan ditambahkan secara otomatis ke inventaris outlet.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Simpan Penerimaan!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    setLoadingSubmit(true);
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const payload = {
      received: isFullyReceived,
      items: formData.map((item) => ({
        id: item.id,
        qty: Number(item.qtyReceive),
        expired_at: item.expiredAt,
      })),
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}/receive`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.message || "Gagal menyimpan penerimaan barang");
      }

      await Swal.fire({
        title: "Penerimaan Berhasil!",
        text: `Data barang untuk PO ${poNumber} telah dicatat.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      navigate("/inventory/purchase-order");
    } catch (err) {
      console.error("Receive PO error:", err);
      Swal.fire({
        title: "Gagal Menyimpan",
        text: err.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/purchase-order")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Penerimaan Barang PO: {poNumber}
            </h1>
            <p className="text-xs text-gray-500">
              Isi kuantitas fisik dan tanggal expired obat yang diterima di outlet{" "}
              <span className="font-bold text-emerald-700">{outlet.name}</span>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
      >
        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-left text-xs text-gray-600">
            <thead>
              <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="p-3.5 w-10 text-center">No</th>
                <th className="p-3.5">Nama Obat & Log Penerimaan</th>
                <th className="p-3.5 text-center">Qty Dipesan</th>
                <th className="p-3.5 text-center">Qty Diterima</th>
                <th className="p-3.5 text-center">Qty Input Baru</th>
                <th className="p-3.5">Tanggal Expired</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {formData.map((item, idx) => (
                <tr key={item.id || idx} className="hover:bg-emerald-50/20 transition-colors">
                  <td className="p-3.5 text-center font-bold text-gray-400">{idx + 1}</td>
                  <td className="p-3.5 space-y-1">
                    <div className="font-extrabold text-gray-900 text-sm">
                      {item.name} <span className="text-gray-400 text-xs">{item.dosis}</span>
                    </div>
                    {item.logs && item.logs.length > 0 ? (
                      <div className="space-y-1 pt-1">
                        {item.logs.map((log, li) => (
                          <div
                            key={li}
                            className="text-[11px] text-teal-700 bg-teal-50 px-2.5 py-1 rounded-xl flex items-center gap-1.5 w-fit border border-teal-100 font-semibold"
                          >
                            <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                            <span>
                              Terima: {log.qty} pcs —{" "}
                              {new Date(log.log_at).toLocaleString("id-ID", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-100">
                        Belum pernah diterima
                      </span>
                    )}
                  </td>
                  <td className="p-3.5 text-center font-mono font-bold text-gray-800">
                    {item.qtyOrder}
                  </td>
                  <td className="p-3.5 text-center font-mono font-bold text-teal-700">
                    {item.totalReceived}
                  </td>
                  <td className="p-3.5">
                    <div className="space-y-1 text-center">
                      <input
                        type="number"
                        min={0}
                        max={item.remainingQty}
                        value={item.qtyReceive}
                        onChange={(e) => handleChange(idx, "qtyReceive", e.target.value)}
                        className="w-24 p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono font-extrabold text-center text-gray-900 focus:outline-none focus:border-emerald-500"
                      />
                      <p className="text-[10px] font-semibold text-gray-400">
                        Sisa Max: {item.remainingQty}
                      </p>
                    </div>
                  </td>
                  <td className="p-3.5">
                    <input
                      type="date"
                      value={item.expiredAt}
                      onChange={(e) => handleChange(idx, "expiredAt", e.target.value)}
                      className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:border-emerald-500"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate("/inventory/purchase-order")}
            className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
          >
            Batal
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={confirmSubmit}
            disabled={loadingSubmit}
            className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
          >
            <FiSave className="w-4 h-4" />
            <span>{loadingSubmit ? "Menyimpan..." : "Simpan Penerimaan Barang"}</span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReceivePO;
